import React, { useEffect, useMemo, useState } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  FormControl,
  InputAdornment,
  Select,
  MenuItem,
  Chip,
  Grid,
  Avatar,
  Switch,
  FormHelperText,
  CircularProgress
} from "@mui/material";

import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { frFR, enUS } from "@mui/x-data-grid/locales";
import { Add, Edit, Delete, Close, CloudUpload } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";

import { useTranslation } from "react-i18next";
import { GlobalStyles, useTheme, useMediaQuery } from '@mui/material';
import { Visibility, VisibilityOff } from "@mui/icons-material";
import usePermissions from "../../hooks/usePermissions";
import { fetchVipBanners, fetchVipBannerById, createVipBanner, updateVipBanner, deleteVipBanner, toggleVipBannerStatus } from "../../Redux/Reducers/VipBannersReducer";
import { formatUTCDate, formatUTCDateForDataGrid } from "../../utils/dateUtils";
import { Link } from "react-router-dom";
import { height, width } from "@mui/system";

const MODULE_KEY = "vip-banners";

const SCREENS = [
  { label: "HOME", value: "HOME" },
  { label: "PLAYER", value: "PLAYER" },
  { label: "ALBUM", value: "ALBUM" },
  { label: "CATEGORY", value: "CATEGORY" },
  { label: "SEARCH", value: "SEARCH" },
];
const BANNER_TYPES = [
  { label: "Static", value: "STATIC" },
  { label: "Partner", value: "PARTNER" },
  { label: "Promotional", value: "PROMOTIONAL" },
];

const USER_TYPES = [
  { label: "All", value: "ALL" },
  { label: "Free", value: "FREE_USER" },
  { label: "Premium", value: "PREMIUM_USER" },
];

const ACTION_TYPES = [
  { label: "None", value: "NONE" },
  { label: "External URL", value: "EXTERNAL_URL" },
  // { label: "Internal Screen", value: "INTERNAL_SCREEN" },
];

const PLACEMENTS = [
  { label: "Top", value: "TOP" },
  { label: "Bottom", value: "BOTTOM" },
  { label: "Left", value: "LEFT" },
  { label: "Right", value: "RIGHT" },
  { label: "Center", value: "CENTER" },
];

const FREQUENCY_TYPES = [
  { label: "Always show", value: "always" },
  { label: "Limited impressions per user", value: "limited" }
];

// const PRIORITY_TYPES = [
//   { label:"Low", value: "LOW" },
//   { label:"Medium", value: "MEDIUM" },
//   { label:"High", value: "HIGH" }
// ];

function VipBannersManagement() {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  console.log(i18n.language);
  console.log(t("advertising.vip_banners.title"));
  const { hasAction } = usePermissions();
  const { banners, loading, pagination } = useSelector((state) => state.vipBanners || {});
  const [modalOpen, setModalOpen] = useState(false);
  const [editingVipBanner, setEditingVipBanner] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedViewBanner, setSelectedViewBanner] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    id: null,
    newStatus: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);

  const formatDateForInput = (date) => date ? new Date(date).toISOString().split("T")[0] : "";

  // For Table Thead Width
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const isLarge = useMediaQuery(theme.breakpoints.up('lg'));

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const {
    control,
    watch,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    formState: { errors }
  } = useForm({
    defaultValues: {
      title: "",
      bannerType: "",
      actionType: "",
      // internalScreen: "",
      externalUrl: "",
      userType: "",
      startDate: "",
      endDate: "",
      banner_event_date: "",
      location: "",
      banner_image: "",
      placements: [{ screen: "HOME", position: "CENTER" }],
      priority: "HIGH",
      userType: "",
      isActive: true
    }
  });


  useEffect(() => {
    dispatch(
      fetchVipBanners({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        search: searchText,
      })
    );
  }, [paginationModel, searchText, dispatch]);

  // Action Type
  const selectedActionType = watch("actionType");
  const selectedStartDate = watch("startDate");

  useEffect(() => {
    const selectedEndDate = watch("endDate");
    if (selectedStartDate && selectedEndDate && selectedEndDate < selectedStartDate) {
      setValue("endDate", "");
    }
  }, [selectedStartDate]);

  const vipBannersList = useMemo(() => {
    if (!banners) return [];
    if (Array.isArray(banners)) return banners;
    if (banners.data && Array.isArray(banners.data)) return banners.data;
    return [];
  }, [banners]);

  const handleOpenModal = (row = null) => {
    if (row) {
      setEditingVipBanner(row);
      reset({
        title: row.title || "",
        bannerType: row.bannerType || "",
        actionType: row.actionType || "",
        // internalScreen: row.internalScreen || "",
        externalUrl: row.externalUrl || "",
        userType: row.userType || "",
        startDate: formatDateForInput(row.startDate),
        endDate: formatDateForInput(row.endDate),
        banner_event_date: row.displayedDate ? formatDateForInput(row.displayedDate) : row.banner_event_date ? formatDateForInput(row.banner_event_date) : "",
        location: row.location || "",
        banner_image: null,
        isActive: row.isActive ?? true,
        placements: row.placements || [{ screen: "", position: "" }],
        priority: row.priority || "HIGH"
      });

      setImagePreview(row?.bannerImage ? `${row?.bannerImage}` : null);
    } else {
      setEditingVipBanner(null);
      reset({
        title: "",
        bannerType: "",
        actionType: "",
        // internalScreen: "",
        externalUrl: "",
        userType: "",
        startDate: "",
        endDate: "",
        banner_event_date: "",
        location: "",
        banner_image: null,
        isActive: true,
        placements: [{ screen: "HOME", position: "CENTER" }],
        priority: "HIGH"
      });
      setImagePreview(null);
    }
    setModalOpen(true);
  };
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    const isTypeValid = allowedTypes.includes(file.type);
    const isSizeValid = file.size <= 5 * 1024 * 1024;

    setValue('banner_image', file, {
      shouldValidate: true,
      shouldDirty: true,
    });

    if (isTypeValid && isSizeValid) {
      setImagePreview(URL.createObjectURL(file));
    } else {
      setImagePreview(null);
      e.target.value = null;
    }
  };
  const handleCloseModal = () => setModalOpen(false);

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      const placementKeys = new Set();

      for (let i = 0; i < data.placements.length; i++) {
        const key = `${data.placements[i].screen}-${data.placements[i].position}`;

        if (placementKeys.has(key)) {
          setError(`placements.${i}.position`, {
            type: 'manual',
            message: 'Duplicate placement is not allowed'
          });
          setSubmitting(false);
          return;
        }

        placementKeys.add(key);
      }

      const payload = new FormData();
      payload.append("title", data.title);
      payload.append("banner_type", data.bannerType);
      payload.append("action_type", data.actionType);
      if (data.externalUrl?.trim()) {
        payload.append("external_url", data.externalUrl);
      }
      payload.append("user_type", data.userType);
      payload.append("start_date", data.startDate);
      payload.append("end_date", data.endDate);
      payload.append("banner_event_date", data.banner_event_date || "");
      payload.append("location", data.location);
      payload.append("priority", data.priority || "HIGH");
      payload.append("is_active", data.isActive === true);
      payload.append("placements", JSON.stringify(data.placements));

      if (data.banner_image) payload.append("banner_image", data.banner_image);

      if (editingVipBanner) {
        await dispatch(
          updateVipBanner({ id: editingVipBanner.id, formData: payload })
        ).unwrap();
        toast.success(t('advertising.vip_banners.messages.update_success', 'Vip Banner updated successfully'));
      } else {
        await dispatch(createVipBanner(payload)).unwrap();
        toast.success(t('advertising.vip_banners.messages.create_success', 'Vip Banner created successfully'));
      }
      handleCloseModal();
      dispatch(
        fetchVipBanners({
          page: paginationModel.page + 1,
          limit: paginationModel.pageSize,
          search: searchText,
        })
      );
    } catch (err) {
      if (!err?.errors) {
        toast.error(err?.message || "Something went wrong");
      }

      // Handle field errors
      if (err?.errors) {
        Object.keys(err.errors).forEach((key) => {
          setError(key, {
            type: "server",
            message: err.errors[key][0],
          });
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setDeleteDialogOpen(true);
  };

  const handleViewClick = (banner) => {
    setSelectedViewBanner(banner);
    setViewModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await dispatch(deleteVipBanner({ id: selectedId })).unwrap();
      toast.success(t('advertising.vip_banners.messages.delete_success', 'Vip Banner deleted successfully'));
      dispatch(
        fetchVipBanners({
          page: paginationModel.page + 1,
          limit: paginationModel.pageSize,
          search: searchText,
        })
      );
    } catch {
      toast.error(t('advertising.vip_banners.messages.delete_failed', 'Delete failed'));
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleConfirmStatusUpdate = async () => {
    setStatusUpdating(true);
    try {
      await dispatch(
        toggleVipBannerStatus({
          id: confirmDialog.id,
          isActive: confirmDialog.newStatus,
        })
      ).unwrap();

      toast.success(
        t("customers.messages.status_update_success", "Status updated successfully")
      );
    } catch (err) {
      toast.error(
        err?.message ||
        t("customers.messages.status_update_error", "Failed to update status")
      );
    } finally {
      setStatusUpdating(false);
      setConfirmDialog({ open: false, id: null, newStatus: null });
    }
  };

  const columns =
    [
      {
        field: "sno",
        headerName: t('sno', 'S.No'),
        width: 80,
        sortable: false
      },
      {
        field: "title",
        headerName: t('advertising.vip_banners.fields.title', 'Title'),
        ...(isMobile && { width: 150 }),
        ...(isLarge && { flex: 1 }),
      },
      {
        field: "startDate",
        headerName: t('advertising.vip_banners.fields.startDate', 'Start Date'),
        ...(isMobile && { width: 150 }),
        ...(isLarge && { flex: 1 }),
        valueFormatter: (value) => {
          return formatUTCDate(value, { format: 'date' });
        }
      },
      {
        field: "endDate",
        headerName: t('advertising.vip_banners.fields.endDate', 'End Date'),
        ...(isMobile && { width: 150 }),
        ...(isLarge && { flex: 1 }),
        valueFormatter: (value) => {
          return formatUTCDate(value, { format: 'date' });
        }
      },
      {
        field: "displayedDate",
        headerName: t('advertising.vip_banners.fields.displayDate', 'Displayed Date'),
        ...(isMobile && { width: 150 }),
        ...(isLarge && { flex: 1 }),
        valueFormatter: (value) => {
          return value ? formatUTCDate(value, { format: 'date' }) : "-";
        }
      },
      {
        field: "isActiveVal",
        headerName: t('advertising.vip_banners.fields.status', "Status"),
        ...(isMobile && { width: 150 }),
        ...(isLarge && { flex: 1 }),
        renderCell: (params) => {
          return (
            <Switch
              checked={params.row.isActive}
              onChange={(e) =>
                setConfirmDialog({
                  open: true,
                  id: params.row.id,
                  newStatus: e.target.checked,
                })
              }
              color="success"
            />
          );
        }
      },
      {
        field: "createdAt",
        headerName: t('advertising.vip_banners.fields.createdAt', 'Created At'),
        ...(isMobile && { width: 150 }),
        ...(isLarge && { flex: 1 }),
        valueFormatter: (value) => {
          return formatUTCDateForDataGrid(value);
        }
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 140,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: '6px' }}>
            <IconButton
              size="small"
              onClick={() => handleViewClick(params.row)}
              sx={{ color: '#ffffff', backgroundColor: 'rgba(227, 214, 245, 0.2) !important', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(227, 214, 245, 0.1)', padding: '0' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="13" viewBox="0 0 18 13" fill="none">
                <path d="M17.415 4.66071C15.5893 1.74214 12.4457 0 9 0C5.55429 0 2.41071 1.74214 0.585 4.66071L0.437143 4.89857C0.154286 5.36143 0 5.88857 0 6.42857C0 6.96857 0.154286 7.49571 0.437143 7.95857L0.585 8.19643C2.41071 11.115 5.55429 12.8571 9 12.8571C12.4457 12.8571 15.5893 11.115 17.415 8.19643L17.5629 7.95857C17.8457 7.49571 18 6.96857 18 6.42857C18 5.88857 17.8457 5.36143 17.5629 4.89857L17.415 4.66071ZM16.47 7.27714L16.3221 7.515C14.7343 10.0543 11.9957 11.5714 9 11.5714C6.00429 11.5714 3.26571 10.0543 1.67786 7.515L1.53 7.27714C1.36929 7.02 1.28571 6.73071 1.28571 6.42857C1.28571 6.12643 1.36929 5.83714 1.53 5.58L1.67786 5.34214C3.26571 2.80286 6.00429 1.28571 9 1.28571C11.9957 1.28571 14.7343 2.80286 16.3221 5.34214L16.47 5.58C16.6307 5.83714 16.7143 6.12643 16.7143 6.42857C16.7143 6.73071 16.6307 7.02 16.47 7.27714Z" fill="#ffffff" />
                <path d="M8.99972 2.57129C6.87186 2.57129 5.14258 4.30057 5.14258 6.42843C5.14258 8.55629 6.87186 10.2856 8.99972 10.2856C11.1276 10.2856 12.8569 8.55629 12.8569 6.42843C12.8569 4.30057 11.1276 2.57129 8.99972 2.57129ZM8.99972 8.99986C7.57901 8.99986 6.42829 7.84915 6.42829 6.42843C6.42829 5.00772 7.57901 3.857 8.99972 3.857C10.4204 3.857 11.5711 5.00772 11.5711 6.42843C11.5711 7.84915 10.4204 8.99986 8.99972 8.99986Z" fill="#ffffff" />
              </svg>
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleOpenModal(params.row)}
              sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(227, 214, 245, 0.1)', padding: '0' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path fillRule="evenodd" clipRule="evenodd" d="M15.531 3.84226C13.9473 5.42594 10.2302 9.14231 9.3237 10.0459C9.12779 10.2411 8.89463 10.3952 8.64435 10.4958C7.91732 10.7975 6.14371 11.4932 5.62228 11.5283C5.31165 11.5491 5.00699 11.4344 4.7865 11.2139C4.566 10.9934 4.45129 10.6887 4.47215 10.3781C4.50716 9.85668 5.2029 8.08453 5.49937 7.36048C5.60291 7.10348 5.7571 6.87032 5.95301 6.67516L12.1581 0.469295C12.4582 0.169095 12.8665 0 13.291 0C13.7156 0 14.1239 0.169095 14.424 0.469295L15.531 1.57623C15.8312 1.87643 16.0003 2.28465 16.0003 2.70925C16.0003 3.13384 15.8312 3.54206 15.531 3.84226ZM5.59174 10.4087C6.10125 10.3446 7.59031 9.72334 8.21603 9.46336C8.21826 9.46262 8.2205 9.46187 8.22199 9.46113C8.33894 9.4142 8.44471 9.34418 8.53336 9.25553L8.5341 9.25479C9.44214 8.35121 13.1577 4.63559 14.7406 3.0519C14.8315 2.96103 14.8829 2.83811 14.8829 2.70925C14.8829 2.58038 14.8315 2.45746 14.7406 2.36659L13.6337 1.25965C13.5428 1.16877 13.4199 1.11737 13.291 1.11737C13.1622 1.11737 13.0393 1.16877 12.9484 1.25965L6.74261 7.46551C6.65322 7.55415 6.5832 7.66068 6.53627 7.77763L6.53478 7.78061C6.27779 8.40782 5.6558 9.89839 5.59174 10.4087Z" fill="white" />
                <path fillRule="evenodd" clipRule="evenodd" d="M14.0542 4.52797C14.2717 4.74623 14.2717 5.10006 14.0542 5.31832C13.8359 5.53658 13.4821 5.53658 13.2638 5.31832L10.6813 2.73571C10.463 2.51745 10.463 2.16362 10.6813 1.94536C10.8995 1.72785 11.2534 1.72785 11.4716 1.94536L14.0542 4.52797Z" fill="white" />
                <path fillRule="evenodd" clipRule="evenodd" d="M9.49485 9.08712C9.71311 9.30464 9.71311 9.65922 9.49485 9.87673C9.27734 10.095 8.92277 10.095 8.70525 9.87673L6.12268 7.29412C5.90442 7.07661 5.90442 6.72203 6.12268 6.50451C6.34019 6.28626 6.69476 6.28626 6.91227 6.50451L9.49485 9.08712Z" fill="white" />
                <path fillRule="evenodd" clipRule="evenodd" d="M5.40054 1.47412C5.70893 1.47412 5.95922 1.72441 5.95922 2.03281C5.95922 2.3412 5.70893 2.59149 5.40054 2.59149H2.79339C1.86747 2.59149 1.11735 3.34162 1.11735 4.26754V13.2065C1.11735 13.6512 1.2939 14.0773 1.60824 14.3916C1.92259 14.706 2.34868 14.8825 2.79339 14.8825H12.4771C12.9218 14.8825 13.3479 14.706 13.6623 14.3916C13.9766 14.0773 14.1532 13.6512 14.1532 13.2065V10.5993C14.1532 10.2909 14.4034 10.0406 14.7118 10.0406C15.0202 10.0406 15.2705 10.2909 15.2705 10.5993V13.2065C15.2705 13.9477 14.9763 14.6576 14.4526 15.182C13.9282 15.7057 13.2183 15.9999 12.4771 15.9999H2.79339C2.05221 15.9999 1.34231 15.7057 0.817903 15.182C0.294237 14.6576 0 13.9477 0 13.2065V4.26754C0 2.72483 1.25069 1.47412 2.79339 1.47412H5.40054Z" fill="white" />
              </svg>
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(params.row.id)}
              sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(227, 214, 245, 0.1)', padding: '0' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="16" viewBox="0 0 15 16" fill="none">
                <path d="M7.21951 1.17073C6.4558 1.17073 5.80457 1.65856 5.56322 2.34141C5.45548 2.64622 5.12105 2.80599 4.81623 2.69825C4.51142 2.59051 4.35166 2.25608 4.4594 1.95127C4.86091 0.81529 5.94427 0 7.21951 0C8.49483 0 9.57815 0.81529 9.97971 1.95127C10.0874 2.25608 9.92765 2.59051 9.62287 2.69825C9.31801 2.80599 8.98357 2.64622 8.87586 2.34141C8.63454 1.65856 7.98322 1.17073 7.21951 1.17073Z" fill="white" />
                <path d="M0 3.70744C0 3.38415 0.26208 3.12207 0.585366 3.12207H13.8537C14.177 3.12207 14.4391 3.38415 14.4391 3.70744C14.4391 4.03072 14.177 4.2928 13.8537 4.2928H0.585366C0.26208 4.2928 0 4.03072 0 3.70744Z" fill="white" />
                <path d="M2.47023 5.61969C2.44873 5.29712 2.1698 5.05306 1.84723 5.07456C1.52466 5.09606 1.28059 5.37499 1.3021 5.69757L1.66382 11.1233C1.73054 12.1245 1.78444 12.9332 1.91086 13.5678C2.04229 14.2275 2.26584 14.7786 2.72758 15.2106C3.1893 15.6426 3.75403 15.8289 4.42107 15.9162C5.06266 16.0001 5.87318 16.0001 6.87657 16.0001H7.56246C8.56586 16.0001 9.37632 16.0001 10.018 15.9162C10.685 15.8289 11.2497 15.6426 11.7115 15.2106C12.1732 14.7786 12.3967 14.2275 12.5282 13.5678C12.6545 12.9332 12.7085 12.1245 12.7752 11.1233L13.1369 5.69757C13.1584 5.37499 12.9143 5.09606 12.5918 5.07456C12.2692 5.05306 11.9902 5.29712 11.9688 5.61969L11.6098 11.0043C11.5397 12.0564 11.4896 12.7883 11.38 13.339C11.2735 13.8733 11.125 14.156 10.9116 14.3557C10.6982 14.5553 10.4062 14.6847 9.86607 14.7554C9.30927 14.8282 8.57562 14.8294 7.52133 14.8294H6.9177C5.86342 14.8294 5.12972 14.8282 4.57292 14.7554C4.03282 14.6847 3.7408 14.5553 3.5274 14.3557C3.314 14.156 3.16545 13.8733 3.05903 13.339C2.94932 12.7883 2.89935 12.0564 2.82922 11.0043L2.47023 5.61969Z" fill="white" />
                <path d="M5.20978 7.02736C5.53147 6.9952 5.81828 7.2299 5.85051 7.55161L6.24076 11.4541C6.27291 11.7757 6.03822 12.0626 5.71651 12.0948C5.39484 12.1269 5.10797 11.8922 5.07581 11.5705L4.68557 7.66806C4.65339 7.34642 4.8881 7.05952 5.20978 7.02736Z" fill="white" />
                <path d="M9.75346 7.66806C9.78561 7.34642 9.55092 7.05952 9.22921 7.02736C8.90757 6.9952 8.62066 7.2299 8.5885 7.55161L8.19826 11.4541C8.1661 11.7757 8.4008 12.0626 8.72251 12.0948C9.04415 12.1269 9.33098 11.8922 9.36321 11.5705L9.75346 7.66806Z" fill="white" />
              </svg>
            </IconButton>
          </Box>
        )
      }
    ];

  const rows = useMemo(() => {
    return vipBannersList.map((banner, index) => ({
      id: banner.id,
      sno: paginationModel.page * paginationModel.pageSize + index + 1,
      title: banner.title || "",
      startDate: banner.startDate || "",
      endDate: banner.endDate || "",
      bannerType: banner.bannerType || "",
      actionType: banner.actionType || "",
      externalUrl: banner.externalUrl || "",
      createdAt: banner.createdAt || "",
      // internalScreen: banner.internalScreen || "",
      placements: banner.placements || "",
      priority: banner.priority || "",
      userType: banner.userType || "",
      isActive: banner.isActive || false,
      isActiveVal: banner.isActive === true ? "Active" : "Inactive",
      bannerImage: banner?.bannerImage ? `${banner?.bannerImage}` : null,
      displayedDate: banner.displayedDate || banner.banner_event_date || "",
      location: banner.location || "",
    }))
      .filter((row) => {
        const s = searchText.toLowerCase();
        return (
          row.title.toLowerCase().includes(s) ||
          (row.isActive ? "active" : "inactive").includes(s)
        );
      });
  }, [banners]);

  // Placement Locations 
  const { fields, append, remove } = useFieldArray({
    control,
    name: "placements"
  });



  return (
    <Box sx={{ p: '36px 24px 24px' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: '24px', flexWrap: 'wrap', columnGap: '6px', rowGap: '16px' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', lineHeight: '32px' }} className='main_title'>{t('advertising.vip_banners.title', 'VIP Banners')}</Typography>
        <Box display="flex" gap={2}>
          <TextField
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={t('search', 'Search')}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '999px',
                backgroundColor: 'rgba(230, 210, 249, 0.2)',
                color: '#fff',
                height: '38px',
                '& fieldset': {
                  borderColor: 'rgba(230, 210, 249, 0.1)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(230, 210, 249, 0.1)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(230, 210, 249, 0.1)',
                  borderWidth: '1px',
                },
              },
              '& .MuiOutlinedInput-input': {
                padding: '10px 16px',
                color: '#FF69B4',
                fontSize: '15px',
                lineHeight: '18px',

              },
              '& input::placeholder': {
                color: 'rgba(255,255,255,0.6)',
                opacity: 1,
              },
              '& .MuiFormHelperText-root': {
                ml: '20px'
              }
            }}
          />
          {hasAction(MODULE_KEY, 'create') && (
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenModal()}
              sx={{
                border: '1px solid rgba(230, 210, 249, 0.1) !important',
                color: '#fff',
                textTransform: 'none',
                backgroundColor: '#FF69B4 !important',
                borderRadius: '999px',
                fontSize: '15px',
                lineHeight: '24px',
                padding: '0',
                minWidth: '38px',
                '& .MuiButton-startIcon': {
                  margin: 0,
                },
              }}
            ></Button>
          )}
        </Box>
      </Box>

      <Box sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(230, 210, 249, 0.2)', borderRadius: '10px', px: '12px' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          slots={{ toolbar: GridToolbar }}
          paginationMode="server"
          rowCount={pagination?.total || 0}
          paginationModel={paginationModel}
          onPaginationModelChange={(model) => {
            setPaginationModel(model);
          }}
          slotProps={{
            toolbar: {
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
            },
          }}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
            sorting: { sortModel: [{ field: "createdAt", sort: "desc" }] }
          }}
          getRowId={(row) => row.id || row.sno}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          localeText={i18n.language?.toLowerCase().startsWith('fr') ? frFR.components.MuiDataGrid.defaultProps.localeText : enUS.components.MuiDataGrid.defaultProps.localeText}
          sx={{
            backgroundColor: 'transparent',
            border: 'none',
            borderRadius: '0',
            '& .MuiDataGrid-toolbarContainer': {
              padding: '12px',
              margin: '0 -12px',
              gap: '10px',
              backgroundColor: 'rgba(255,255,255,0.05)',
            },
            '& .MuiDataGrid-topContainer::after': {
              display: 'none',
            },
            '& .MuiDataGrid-toolbarContainer .MuiButton-root': {
              color: '#fff',
              border: '1px solid rgba(230, 210, 249, 0.2)',
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 500,
              padding: '6px 12px',
              backgroundColor: 'rgba(255,255,255,0.05)',
            },
            '& .MuiDataGrid-toolbarContainer .MuiButton-root .MuiButton-icon .MuiBadge-root svg': {
              width: '18px',
              height: '18px',
            },
            '& .MuiDataGrid-virtualScrollerContent': {
              maxHeight: 'auto',
              height: 'auto !important'
            },
            '& .MuiDataGrid-virtualScroller': {
              borderRadius: '0 !important',
            },
            '& .MuiDataGrid-virtualScrollerRenderZone': {
              position: 'inherit',
            },
            '& .MuiDataGrid-virtualScrollerRenderZone>.MuiDataGrid-row': {
              maxHeight: 'none !important',
              height: 'auto !important',
            },
            '& .MuiDataGrid-cell': {
              height: 'auto !important',
              minHeight: 'var(--height)',
              paddingTop: '5px',
              paddingBottom: '5px',
            },
            '& .MuiDataGrid-cell>.MuiBox-root': {
              minHeight: '100%',
              alignItems: 'center',
            },
            '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-cell': {
              borderRadius: '0 !important',
              borderColor: 'rgba(230, 210, 249, 0.2) !important',
              color: '#fff',
            },
            '& .MuiDataGrid-columnHeaders [role=row]': {
              backgroundColor: '#1a1a1a',
            },
            '& .MuiDataGrid-footerContainer': {
              borderColor: 'rgba(230, 210, 249, 0.2) !important'
            },
            '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader': {
              color: 'white',
            },
            '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
              opacity: '0.8 !important',
              fill: 'white',
            },
            '& .MuiDataGrid-cell .MuiChip-outlined': {
              border: '0 !important',
              height: 'auto',
              fontWeight: '600',
            },
            '& .MuiDataGrid-cell .MuiChip-outlined.MuiChip-colorSuccess': {
              color: "#03c11c",
            },
            '& .MuiTablePagination-root': {
              color: '#fff'
            },
            '& .MuiTablePagination-selectLabel': {
              marginBottom: '0',
            },
            '& .MuiTablePagination-input': {
              marginRight: '10px',
            },
            '& .MuiTablePagination-input .MuiSelect-select': {
              paddingTop: '2px',
              paddingBottom: '2px',
              border: '1px solid rgba(230, 210, 249, 0.2)',
              backgroundColor: 'rgba(255,255,255,0.05)',
              borderRadius: '4px',
            },
            '& .MuiTablePagination-input .MuiSvgIcon-root': {
              fill: 'white'
            },
            '& .MuiTablePagination-toolbar .MuiTablePagination-displayedRows': {
              marginBottom: '0',
            },
            '& .MuiTablePagination-actions': {
              marginLeft: '10px !important',
            },
            '& .MuiTablePagination-actions .MuiButtonBase-root': {
              color: '#FF69B4',
            },
            '& .MuiDataGrid-filler': {
              height: 'auto !important',
            },
            '& .MuiDataGrid-filler>div': {
              borderColor: 'transparent',
            },
            '& .MuiDataGrid-overlayWrapper': {
              height: 'auto !important',
              maxHeight: '50px',
            },
            '& .MuiDataGrid-overlayWrapperInner': {
              minHeight: '40px',
              height: 'auto !important'
            },
            '& .MuiDataGrid-overlay': {
              color: '#fff',
              borderTop: '1px solid rgba(230, 210, 249, 0.2)',
              backgroundColor: 'transparent',
              minHeight: '40px',
            },
            // '& .MuiDataGrid-row .MuiDataGrid-cellOffsetLeft + .MuiDataGrid-cell + .MuiDataGrid-cell': {
            //   display: 'flex',
            //   alignItems: 'center',
            //   justifyContent: 'center', 
            // },
          }}
        />
      </Box>

      <Dialog open={modalOpen} onClose={handleCloseModal} fullWidth sx={{
        maxWidth: '810px !important', marginLeft: 'auto', marginRight: 'auto', '& .MuiPaper-root': {
          maxWidth: '710px'
        }
      }}>
        <DialogTitle sx={{ padding: '16px', fontSize: '18px', }}>
          {editingVipBanner
            ? t('advertising.vip_banners.popup_titles.edit', 'Edit Banner')
            : t('advertising.vip_banners.popup_titles.add', 'Create Banner')}
          <IconButton onClick={handleCloseModal} sx={{ position: "absolute", right: '16px', top: '20px', padding: '0', color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <Box
          component="form"
          id="vip-banner-form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
          }}
        >
          <DialogContent
            dividers
            sx={{
              borderColor: 'rgba(230, 210, 249, 0.1)',
              padding: '24px 16px',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(230, 210, 249, 0.3)',
                borderRadius: '4px',
                '&:hover': {
                  background: 'rgba(230, 210, 249, 0.5)',
                },
              },
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {/* Title */}
              <Controller
                name="title"
                control={control}
                rules={{
                  required: t('advertising.vip_banners.validations.title_required', 'Title is required'),
                  minLength: {
                    value: 3,
                    message: t('advertising.vip_banners.validations.title_min', 'Minimum 3 characters'),
                  },
                  maxLength: {
                    value: 100,
                    message: t('advertising.vip_banners.validations.title_max', 'Maximum 100 characters allowed'),
                  },
                  validate: (value) =>
                    value?.trim()?.length > 0 || t('advertising.vip_banners.validations.title_empty', 'Title cannot be empty'),
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.title}
                    helperText={errors.title?.message}
                    placeholder={t('advertising.vip_banners.fields.title', 'Banner Title')}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-input': {
                        padding: '12px 20px',
                        fontWeight: '400',
                        color: '#fff',
                      },
                      '& input::placeholder': {
                        color: 'rgba(255,255,255,0.6)',
                        opacity: 1,
                      },
                    }}
                  />
                )}
              />

              {/* Image Upload */}
              <Controller
                name="banner_image"
                control={control}
                rules={{
                  validate: {
                    required: (value) => {
                      if (!editingVipBanner && !value) {
                        return t('advertising.vip_banners.validations.banner_image_required', 'Banner image is required');
                      }
                      return true;
                    },

                    fileSize: (value) => {
                      if (!value || typeof value === "string") return true;

                      return value.size <= 5 * 1024 * 1024
                        ? true
                        : t('advertising.vip_banners.validations.file_size', 'File must be less than 5MB');
                    },

                    fileType: (value) => {
                      if (!value || typeof value === "string") return true;

                      const allowedTypes = [
                        "image/jpeg",
                        "image/png",
                        "image/webp",
                      ];

                      return allowedTypes.includes(value.type)
                        ? true
                        : t('advertising.vip_banners.validations.file_type', 'Only JPG, PNG and WEBP files are allowed');
                    },
                  },
                }}
                render={({ field }) => (
                  <Grid item xs={12} sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: '400', fontSize: '14px' }}>
                        {t("administrator.fields.upload_image_title", "Upload Image")} <Typography component="span" sx={{ fontSize: '13px', color: '#ff4d4f', ml: 1 }}>{t('administrator.fields.upload_square_only', '(Upload Only Square Image)')}</Typography>
                    </Typography>
                    <Box component="label"
                      onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const file = e.dataTransfer.files[0];
                          if (file) {
                              handleImageChange({ target: { files: [file] } });
                          }
                      }}
                      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      sx={{
                        border: "1px dashed rgba(230, 210, 249, 0.2)",
                        p: 3,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexWrap: "wrap",
                        cursor: "pointer",
                        borderRadius: "999px",
                        flexDirection: "column"
                      }}
                    >
                      <input hidden type="file" accept="image/png, image/jpg, image/webp" onChange={handleImageChange} />
                      {imagePreview ? (
                        <img src={imagePreview} alt="preview" style={{ width: 150, height: 80, objectFit: "cover" }} />
                      ) : (
                        <>
                          <Box sx={{ pointerEvents: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <CloudUpload fontSize="large" />
                            <Typography sx={{ mb: 0, fontWeight: '400', fontSize: '14px', display: 'block' }}>
                              {t('advertising.vip_banners.fields.upload_image', "Upload Image (File must be less than 5MB)")}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '13px', color: '#ff4d4f', textAlign: 'center', mb: '4px' }}>
                              {t('administrator.fields.upload_square_only', '(Upload Only Square Image)')}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '12px', color: "rgba(255,255,255,0.6)", textAlign: 'center' }}>
                              {t('advertising.vip_banners.popup_titles.click_browse', "or click to browse (Supported formats: PNG, JPG, WEBP)")}
                            </Typography>
                          </Box>
                        </>
                      )}
                    </Box>

                    {/* Optional error */}
                    {errors.banner_image && (
                      <Typography color="error" variant="caption" sx={{ mx: '14px' }}>
                        {errors.banner_image.message}
                      </Typography>
                    )}
                  </Grid>
                )}
              />

              {/* Banner Type */}
              <Controller
                name="bannerType"
                control={control}
                defaultValue=""
                rules={{
                  required: t('advertising.vip_banners.validations.banner_type_required', 'Banner type is required'),
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    error={!!errors.bannerType}
                    helperText={errors.bannerType?.message}
                    placeholder={t('advertising.vip_banners.fields.bannerType', 'Banner Type')}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-input': {
                        padding: '12px 20px',
                        fontWeight: '400',
                        color: '#fff',
                      },
                      '& input::placeholder': {
                        color: 'rgba(255,255,255,0.6)',
                        opacity: 1,
                      },
                    }}
                  />
                )}
              />

              {/* Placement Locations */}
              {/* <Box>
                  <Typography variant="subtitle1" sx={{ fontSize: '16px', color: 'white', fontWeight: '400', mb: '5px' }}>{t('advertising.vip_banners.fields.placement_locations', 'Placement Locations')}</Typography>
                  {fields.map((item, index) => (
                    <Box key={item.id} sx={{ display: 'flex', gap: '16px', mb: 3, flexDirection: { xs: 'column', md: 'row' }, }}>
                      <Controller
                        name={`placements.${index}.screen`}
                        control={control}
                        rules={{
                            required: t(
                                'advertising.vip_banners.validations.screen_required',
                                'Screen is required'
                            )
                        }}
                        render={({ field }) => (
                          <FormControl fullWidth error={!!errors?.placements?.[index]?.screen}>
                            <Select
                              {...field}
                              error={!!errors?.placements?.[index]?.screen}
                              displayEmpty
                              renderValue={(selected) =>
                                !selected ? (
                                  <span style={{ color: "#aaa" }}>
                                    {t('advertising.vip_banners.fields.screen', "Screen Name")}
                                  </span>
                                ) : (
                                  selected
                                )
                              }
                              sx={{
                                color: "#fff",
                                fontWeight: 400,
                                "& .MuiSelect-select": {
                                  padding: "12px 20px",
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                  border: "1px solid rgba(230, 210, 249, 0.1) !important",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  border: "1px solid rgba(230, 210, 249, 0.1)",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                  border: "1px solid rgba(230, 210, 249, 0.1)",
                                },
                                "& .MuiSvgIcon-root": {
                                  color: "#fff",
                                },
                              }}
                            >
                              <MenuItem value="" disabled>{t('advertising.vip_banners.fields.screen', "Screen Name")}</MenuItem>
                              {SCREENS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                    </MenuItem>
                              ))}
                            </Select>

                            {errors?.placements?.[index]?.screen && (
                              <FormHelperText>
                                  <Typography color="error" variant="caption">
                                      {errors.placements[index].screen.message}
                                  </Typography>
                              </FormHelperText>
                            )}
                          </FormControl>
                        )}
                      />
                      <Controller
                        name={`placements.${index}.position`}
                        control={control}
                        rules={{
                            required: t(
                                'advertising.vip_banners.validations.position_required',
                                'Placement is required'
                            )
                        }}
                        render={({ field }) => (
                          <FormControl fullWidth error={!!errors?.placements?.[index]?.position}>
                            <Select
                              {...field}
                              error={!!errors?.placements?.[index]?.position}
                              displayEmpty
                              sx={{
                                color: "#fff",
                                fontWeight: 400,
                                "& .MuiSelect-select": {
                                  padding: "12px 20px",
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                  border: "1px solid rgba(230, 210, 249, 0.1) !important",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  border: "1px solid rgba(230, 210, 249, 0.1)",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                  border: "1px solid rgba(230, 210, 249, 0.1)",
                                },
                                "& .MuiSvgIcon-root": {
                                  color: "#fff",
                                },
                              }}
                            >
                              <MenuItem value="" disabled>
                                {t('advertising.vip_banners.fields.placement', "Placement")}
                              </MenuItem>
                              {PLACEMENTS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors?.placements?.[index]?.position && (
                              <FormHelperText>
                                <Typography color="error" variant="caption">
                                  {errors.placements[index].position.message}
                                </Typography>
                              </FormHelperText>
                            )}
                          </FormControl>
                        )}
                      />
                      <Box>
                        {index === 0 ? (
                          <Button type="button" onClick={() => append({ screen: "", position: "" })} sx={{ width: '44px', height: '44px', minWidth: '44px', backgroundColor: '#FF69B4 !important', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: '0', padding: '0', color: '#000', fontSize: '30px' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M15.1999 7.19998H8.80002V0.799949C8.80002 0.358446 8.44158 0 7.99993 0C7.55842 0 7.19998 0.358446 7.19998 0.799949V7.19998H0.799949C0.358446 7.19998 0 7.55842 0 7.99993C0 8.44158 0.358446 8.80002 0.799949 8.80002H7.19998V15.1999C7.19998 15.6416 7.55842 16 7.99993 16C8.44158 16 8.80002 15.6416 8.80002 15.1999V8.80002H15.1999C15.6416 8.80002 16 8.44158 16 7.99993C16 7.55842 15.6416 7.19998 15.1999 7.19998Z" fill="black"/>
                            </svg>
                          </Button>
                        ) : (
                          <Button type="button" onClick={() => remove(index)} sx={{ width: '44px', height: '44px', minWidth: '44px', backgroundColor: 'transparent !important', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: '0', padding: '0', color: '#000', fontSize: '30px' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                              <path d="M10.4163 0.234311L5.89086 4.75971L1.36535 0.234207C1.05316 -0.0779822 0.546244 -0.0779822 0.233951 0.234311C-0.0782386 0.5465 -0.0782386 1.05342 0.233951 1.36561L4.75945 5.89111L0.233951 10.4166C-0.0782386 10.7288 -0.0782386 11.2357 0.233951 11.5479C0.546244 11.8602 1.05316 11.8602 1.36535 11.548L5.89086 7.02252L10.4163 11.5479C10.7286 11.8602 11.2355 11.8602 11.5477 11.548C11.86 11.2357 11.86 10.7288 11.5477 10.4165L7.02226 5.89111L11.5477 1.36571C11.86 1.05342 11.86 0.5465 11.5477 0.234207C11.2355 -0.0779822 10.7286 -0.0779822 10.4163 0.234311Z" fill="white"/>
                            </svg>
                          </Button>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box> */}

              {/* Action Type */}
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Controller
                  name="actionType"
                  control={control}
                  rules={{
                    required: t('advertising.vip_banners.validations.action_type_required', 'Action type is required'),
                  }}
                  defaultValue=""
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors?.actionType} sx={{ mb: 2 }}>
                      <Select
                        {...field}
                        displayEmpty
                        sx={{
                          color: "#fff",
                          fontWeight: 400,
                          "& .MuiSelect-select": {
                            padding: "12px 20px",
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "1px solid rgba(230, 210, 249, 0.1) !important",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            border: "1px solid rgba(230, 210, 249, 0.1)",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            border: "1px solid rgba(230, 210, 249, 0.1)",
                          },
                          "& .MuiSvgIcon-root": {
                            color: "#fff",
                          },
                        }}
                      >
                        <MenuItem value="" disabled>
                          {t('advertising.vip_banners.fields.action_type', "Action Type")}
                        </MenuItem>
                        {ACTION_TYPES.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.actionType && (
                        <FormHelperText error>
                          {errors.actionType.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />

                {/* If External URL Select */}
                {selectedActionType === 'EXTERNAL_URL' && (
                  <Controller
                    name="externalUrl"
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (selectedActionType === "EXTERNAL_URL") {
                          if (!value?.trim()) {
                            return t('advertising.vip_banners.validations.url_required', 'URL is required');
                          }

                          try {
                            const trimmedUrl = value.trim();
                            const parsed = new URL(trimmedUrl);
                            if (!['http:', 'https:'].includes(parsed.protocol)) {
                              return t('advertising.vip_banners.validations.url_invalid', 'Enter valid URL');
                            }
                            if (!parsed.hostname || (!parsed.hostname.includes('.') && parsed.hostname !== 'localhost')) {
                              return t('advertising.vip_banners.validations.url_invalid', 'Enter valid URL');
                            }
                          } catch {
                            return t('advertising.vip_banners.validations.url_invalid', 'Enter valid URL');
                          }
                        }

                        return true;
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        error={!!errors.externalUrl}
                        helperText={errors.externalUrl?.message}
                        placeholder={'URL'}
                        sx={{
                          mb: 2,
                          '& .MuiOutlinedInput-input': {
                            padding: '12px 20px',
                            fontWeight: '400',
                            color: '#fff',
                          },
                          '& input::placeholder': {
                            color: 'rgba(255,255,255,0.6)',
                            opacity: 1,
                          },
                        }}
                      />
                    )}
                  />
                )}

                {/* If Internal Screen Select */}
                {/* {selectedActionType === 'INTERNAL_SCREEN' && (
                    <Controller
                      name="internalScreen"
                      control={control}
                      rules={{
                        validate: (value) => {
                          if (selectedActionType === "INTERNAL_SCREEN") {
                            return value?.trim()
                              ? true
                              : "Screen/App route is required";
                          }

                          return true;
                        },
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          error={!!errors.internalScreen}
                          helperText={errors.internalScreen?.message}
                          placeholder={'App Route / Screen'}
                          sx={{ 
                            mb: 2,
                            '& .MuiOutlinedInput-input': {
                              padding: '12px 20px',
                              fontWeight: '400',
                              color: '#fff',
                            },
                            '& input::placeholder': {
                              color: 'rgba(255,255,255,0.6)',
                              opacity: 1,
                            },
                          }}
                        />
                      )}
                    />
                  )} */}
              </Box>

              {/* Display Rules */}
              <Grid container spacing={2}>
                {/* User Type */}
                <Grid item xs={12} md={12}>
                  <Controller
                    name="userType"
                    control={control}
                    defaultValue=""
                    rules={{
                      required: t('advertising.vip_banners.validations.user_type_required', 'User type is required'),
                    }}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors?.userType} sx={{ mb: 2 }}>
                        <Select
                          {...field}
                          displayEmpty
                          sx={{
                            color: "#fff",
                            fontWeight: 400,
                            "& .MuiSelect-select": {
                              padding: "12px 20px",
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: "1px solid rgba(230, 210, 249, 0.1) !important",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              border: "1px solid rgba(230, 210, 249, 0.1)",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              border: "1px solid rgba(230, 210, 249, 0.1)",
                            },
                            "& .MuiSvgIcon-root": {
                              color: "#fff",
                            },
                          }}
                        >
                          <MenuItem value="" disabled>
                            {t('advertising.vip_banners.fields.userType', "User Type")}
                          </MenuItem>
                          {USER_TYPES.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.userType && (
                          <FormHelperText error>
                            {errors.userType.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>

              </Grid>

              {/* Start/End Date */}
              <Grid container spacing={2}>
                {/* Start Date */}
                <Grid item xs={12} md={6}>
                  <Box position="relative" sx={{ mb: 2 }}>
                    <Controller
                      name="startDate"
                      control={control}
                      rules={{ required: t('advertising.vip_banners.validations.start_date_required', 'Start date is required') }}
                      render={({ field }) => (
                        <>
                          {!field.value && (
                            <span
                              style={{
                                position: "absolute",
                                left: "20px",
                                top: "11px",
                                // transform: "translateY(-50%)",
                                color: "#aaa",
                                pointerEvents: "none",
                                zIndex: 2,
                                fontSize: '14px',
                                backgroundColor: '#252525',
                                paddingRight: '36px',
                              }}
                            >
                              {t('advertising.vip_banners.fields.startDate', "Start Date")}
                            </span>
                          )}

                          <Box
                            component="span"
                            sx={{
                              position: "absolute",
                              right: '3px',
                              top: '2px',
                              width: '40px',
                              height: '40px',
                              backgroundColor: '#252525',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              zIndex: '1',
                              pointerEvents: 'none',
                              borderRadius: '0 20px 20px 0'
                            }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none" style={{ width: "16px", height: "16px" }}>
                              <path d="M5.72217 1V3.55M13.2777 1V3.55" stroke="white" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                              <path opacity="0.4" d="M1.47217 7.02649H17.5277" stroke="white" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M18 6.52502V13.75C18 16.3 16.5833 18 13.2778 18H5.72222C2.41667 18 1 16.3 1 13.75V6.52502C1 3.97502 2.41667 2.27502 5.72222 2.27502H13.2778C16.5833 2.27502 18 3.97502 18 6.52502Z" stroke="white" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                              <path opacity="0.4" d="M12.9889 10.9451H12.9974M12.9889 13.4951H12.9974M9.49542 10.9451H9.50392M9.49542 13.4951H9.50392M6.00098 10.9451H6.01042M6.00098 13.4951H6.00948" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </Box>

                          <TextField
                            {...field}
                            type="date"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-input': {
                                padding: '12px 20px',
                                color: field.value ? '#fff' : 'transparent',
                                fontSize: '14px',
                                minWidth: 'auto',
                                userSelect: 'none'
                              },
                              '& input::-webkit-datetime-edit': {
                                color: field.value ? '#fff' : 'transparent',
                              },
                              '& input::-webkit-calendar-picker-indicator': {
                                filter: 'invert(1)',
                              },
                              '& .MuiTypography-caption': {
                                marginLeft: '14px',
                                marginRight: '14px',
                              }
                            }}
                          />

                          {errors.startDate && (
                            <Typography color="error" variant="caption" sx={{ mx: '14px' }}>
                              {errors.startDate.message}
                            </Typography>
                          )}
                        </>
                      )}
                    />
                  </Box>
                </Grid>

                {/* End Date */}
                <Grid item xs={12} md={6}>
                  <Box position="relative" sx={{ mb: 2 }}>
                    <Controller
                      name="endDate"
                      control={control}
                      rules={{
                        required: t('advertising.vip_banners.validations.end_date_required', 'End date is required'),

                        validate: (value) => {
                          if (!value) return t('advertising.vip_banners.validations.end_date_required', 'End date is required');

                          if (selectedStartDate && value < selectedStartDate) {
                            return t('advertising.vip_banners.validations.end_date_should_before_start_date', 'End date cannot be before start date');
                          }

                          return true;
                        },
                      }}
                      sx={{
                        '& .MuiTypography-caption': {
                          marginLeft: '14px',
                          marginRight: '14px',
                        }
                      }}
                      render={({ field }) => (
                        <>
                          {!field.value && (
                            <span
                              style={{
                                position: "absolute",
                                left: "20px",
                                top: "11px",
                                // transform: "translateY(-50%)",
                                color: "#aaa",
                                pointerEvents: "none",
                                zIndex: 2,
                                fontSize: '14px',
                                backgroundColor: '#252525',
                                paddingRight: '36px',
                              }}
                            >
                              {t('advertising.vip_banners.fields.endDate', "End Date")}
                            </span>
                          )}

                          <Box
                            component="span"
                            sx={{
                              position: "absolute",
                              right: '3px',
                              top: '2px',
                              width: '40px',
                              height: '40px',
                              backgroundColor: '#252525',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              zIndex: '1',
                              pointerEvents: 'none',
                              borderRadius: '0 20px 20px 0'
                            }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none" style={{ width: "16px", height: "16px" }}>
                              <path d="M5.72217 1V3.55M13.2777 1V3.55" stroke="white" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                              <path opacity="0.4" d="M1.47217 7.02649H17.5277" stroke="white" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M18 6.52502V13.75C18 16.3 16.5833 18 13.2778 18H5.72222C2.41667 18 1 16.3 1 13.75V6.52502C1 3.97502 2.41667 2.27502 5.72222 2.27502H13.2778C16.5833 2.27502 18 3.97502 18 6.52502Z" stroke="white" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                              <path opacity="0.4" d="M12.9889 10.9451H12.9974M12.9889 13.4951H12.9974M9.49542 10.9451H9.50392M9.49542 13.4951H9.50392M6.00098 10.9451H6.01042M6.00098 13.4951H6.00948" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </Box>

                          <TextField
                            {...field}
                            type="date"
                            fullWidth
                            inputProps={{
                              min: selectedStartDate || "",
                            }}
                            sx={{
                              '& .MuiOutlinedInput-input': {
                                padding: '12px 20px',
                                color: field.value ? '#fff' : 'transparent',
                                fontSize: '14px',
                                minWidth: 'auto',
                                userSelect: 'none'
                              },
                              '& input::-webkit-datetime-edit': {
                                color: field.value ? '#fff' : 'transparent',
                              },
                              '& input::-webkit-calendar-picker-indicator': {
                                filter: 'invert(1)',
                              },

                            }}
                          />

                          {errors.endDate && (
                            <Typography color="error" variant="caption" sx={{ mx: '14px' }}>
                              {errors.endDate.message}
                            </Typography>
                          )}
                        </>
                      )}
                    />
                  </Box>
                </Grid>
                {/* Display Date on Banner */}
                <Grid item xs={12} md={6}>
                  <Box position="relative" sx={{ mb: 2 }}>
                    <Controller
                      name="banner_event_date"
                      control={control}
                      render={({ field }) => (
                        <>
                          {!field.value && (
                            <span
                              style={{
                                position: "absolute",
                                left: "20px",
                                top: "11px",
                                color: "#aaa",
                                pointerEvents: "none",
                                zIndex: 2,
                                fontSize: '14px',
                                backgroundColor: '#252525',
                                paddingRight: '36px',
                              }}
                            >
                              {t('advertising.vip_banners.fields.displayDate', 'Displayed Date on Banner')}
                            </span>
                          )}

                          <Box
                            component="span"
                            sx={{
                              position: "absolute",
                              right: '3px',
                              top: '2px',
                              width: '40px',
                              height: '40px',
                              backgroundColor: '#252525',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              zIndex: '1',
                              pointerEvents: 'none',
                              borderRadius: '0 20px 20px 0'
                            }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none" style={{ width: "16px", height: "16px" }}>
                              <path d="M5.72217 1V3.55M13.2777 1V3.55" stroke="white" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                              <path opacity="0.4" d="M1.47217 7.02649H17.5277" stroke="white" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M18 6.52502V13.75C18 16.3 16.5833 18 13.2778 18H5.72222C2.41667 18 1 16.3 1 13.75V6.52502C1 3.97502 2.41667 2.27502 5.72222 2.27502H13.2778C16.5833 2.27502 18 3.97502 18 6.52502Z" stroke="white" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                              <path opacity="0.4" d="M12.9889 10.9451H12.9974M12.9889 13.4951H12.9974M9.49542 10.9451H9.50392M9.49542 13.4951H9.50392M6.00098 10.9451H6.01042M6.00098 13.4951H6.00948" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </Box>

                          <TextField
                            {...field}
                            type="date"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-input': {
                                padding: '12px 20px',
                                color: field.value ? '#fff' : 'transparent',
                                fontSize: '14px',
                                minWidth: 'auto',
                                userSelect: 'none'
                              },
                              '& input::-webkit-datetime-edit': {
                                color: field.value ? '#fff' : 'transparent',
                              },
                              '& input::-webkit-calendar-picker-indicator': {
                                filter: 'invert(1)',
                              },
                            }}
                          />
                        </>
                      )}
                    />
                  </Box>
                </Grid>

                {/* Location */}
                <Grid item xs={12} md={6}>
                  <Box position="relative" sx={{ mb: 2 }}>
                    <Controller
                      name="location"
                      control={control}
                      rules={{
                        required: t('advertising.vip_banners.validations.location_required', 'Location '),
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          error={!!errors.location}
                          helperText={errors.location?.message}
                          placeholder={t('advertising.vip_banners.fields.location', 'Location')}
                          sx={{
                            '& .MuiOutlinedInput-input': {
                              padding: '12px 20px',
                              color: '#fff',
                              fontSize: '14px',
                            },
                            '& input::placeholder': {
                              color: 'rgba(255,255,255,0.6)',
                              opacity: 1,
                            },
                          }}
                        />
                      )}
                    />
                  </Box>
                </Grid>
              </Grid>

              {/* Priority */}
              {/* <Controller
                  name="priority"
                  control={control}
                  defaultValue=""
                  rules={{ required: "Priority is required" }}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors?.priority} sx={{ mb: 2 }}>
                      <Select
                        {...field}
                        displayEmpty
                        
                        sx={{
                          color: "#fff",
                          fontWeight: 400,
                          "& .MuiSelect-select": {
                            padding: "12px 20px",
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "1px solid rgba(230, 210, 249, 0.1) !important",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            border: "1px solid rgba(230, 210, 249, 0.1)",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            border: "1px solid rgba(230, 210, 249, 0.1)",
                          },
                          "& .MuiSvgIcon-root": {
                            color: "#fff",
                          },
                        }}
                      >
                        <MenuItem value="" disabled>
                          {t('advertising.vip_banners.fields.priority', "Priority")}
                        </MenuItem>
                        {PRIORITY_TYPES.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                              {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.priority && (
                        <FormHelperText error>
                          {errors.priority.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                /> */}

              {/* Status */}
              <Controller
                name="isActive"
                control={control}
                defaultValue=""
                rules={{
                  validate: (value) => value !== "" || t('advertising.vip_banners.validations.status_required', 'Status is required'),
                }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors?.isActive}>
                    <Select
                      {...field}
                      displayEmpty
                      sx={{
                        color: "#fff",
                        fontWeight: 400,
                        "& .MuiSelect-select": {
                          padding: "12px 20px",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid rgba(230, 210, 249, 0.1) !important",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid rgba(230, 210, 249, 0.1)",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid rgba(230, 210, 249, 0.1)",
                        },
                        "& .MuiSvgIcon-root": {
                          color: "#fff",
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        {t('advertising.vip_banners.fields.status', "Status")}
                      </MenuItem>
                      <MenuItem value={true}>{t('active', 'Active')}</MenuItem>
                      <MenuItem value={false}>{t("inactive", "Inactive")}</MenuItem>
                    </Select>
                    {errors.isActive && (
                      <FormHelperText error>
                        {errors.isActive.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: '16px', py: '20px', flexWrap: 'wrap', gap: '6px' }}>
            <Button
              onClick={handleCloseModal}
              sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}
            >{t('cancel', 'Cancel')}</Button>
            <Button
              variant="contained"
              type="submit"
              disabled={submitting}
              sx={{
                py: 1.5,
                borderRadius: '999px',
                fontWeight: '400',
                fontSize: '15px',
                lineHeight: '20px',
                textTransform: 'none',
                background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
                textTransform: 'capitalize',
                minWidth: { xs: '100%', sm: '150px' },
                marginLeft: '10px',
              }}
            >{submitting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t('save', 'Save')}</Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ padding: '16px', fontSize: '18px', textAlign: 'center', }}>{t('advertising.vip_banners.popup_titles.delete', 'Delete Vip Banner')}</DialogTitle>
        <DialogContent sx={{ pb: '0' }}>
          <Typography sx={{ fontSize: '15px', fontWeight: '400', textAlign: 'center', }}>{t('advertising.vip_banners.messages.delete_confirm', 'Are you sure you want to delete this Vip Banner?')}</Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: '16px',
            py: '20px',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '6px',
          }}
        >
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              backgroundColor: 'rgba(227, 214, 245, 0.2) !important',
              border: '1px solid rgba(227, 214, 245, 0.1)',
              fontSize: '15px',
              lineHeight: '20px',
              fontWeight: '400',
              padding: '11px 20px',
              minWidth: { xs: '100%', sm: '150px' },
              color: 'white',
              borderRadius: '999px',
              textTransform: 'capitalize',
            }}
          >{t('cancel', 'Cancel')}</Button>
          <Button
            variant="contained"
            disabled={deleting}
            onClick={handleDeleteConfirm}
            sx={{
              py: 1.5,
              borderRadius: '999px',
              fontWeight: '400',
              fontSize: '15px',
              lineHeight: '20px',
              background:
                'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
              textTransform: 'capitalize',
              minWidth: { xs: '100%', sm: '150px' },
              marginLeft: '10px',
            }}
          >
            {deleting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t('delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm toggle status  */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, id: null, newStatus: null })}>
        <DialogTitle sx={{ padding: '16px', fontSize: '18px', textAlign: 'center' }}>{t("advertising.vip_banners.popup_titles.confirm_status_update", "Confirm Status Update")}</DialogTitle>
        <DialogContent sx={{ pb: '0' }}>
          <Typography sx={{ fontSize: '15px', fontWeight: '400', textAlign: 'center' }}>{t("advertising.vip_banners.messages.status_update_confirmation", "Are you sure you want to update status of this VIP banner?")}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: '16px', py: '20px', flexWrap: 'wrap', justifyContent: 'center', gap: '6px' }}>
          <Button
            onClick={() =>
              setConfirmDialog({ open: false, id: null, newStatus: null })
            }
            sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}
          >
            {t("cancel", "Cancel")}
          </Button>
          <Button
            variant="contained"
            disabled={statusUpdating}
            onClick={handleConfirmStatusUpdate}
            sx={{
              py: 1.5,
              borderRadius: '999px',
              fontWeight: '400',
              fontSize: '15px',
              lineHeight: '20px',
              background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
              textTransform: 'capitalize',
              minWidth: { xs: '100%', sm: '150px' },
              marginLeft: '10px',
            }}
          >
            {statusUpdating ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t("advertising.vip_banners.actions.update", "Update")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        maxWidth="md"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            backgroundColor: "#252525",
            color: "white",
            borderRadius: "20px",
          },
        }}
      >
        <DialogTitle sx={{ padding: '16px', fontSize: '18px', }}>
          {t('advertising.vip_banners.popup_titles.view', 'View Banner')}
          <IconButton onClick={() => setViewModalOpen(false)} sx={{ position: "absolute", right: '16px', top: '20px', padding: '0', color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: 'rgba(230, 210, 249, 0.1)', '&::-webkit-scrollbar': { width: '8px' }, '&::-webkit-scrollbar-track': { background: '#252525' }, '&::-webkit-scrollbar-thumb': { background: 'rgba(230, 210, 249, 0.2)', borderRadius: '4px' } }}>
          {selectedViewBanner && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="rgba(255,255,255,0.7)">{t('advertising.vip_banners.fields.title', 'Title')}</Typography>
                <Typography variant="body1">{selectedViewBanner.title}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="rgba(255,255,255,0.7)">{t('advertising.vip_banners.fields.status', 'Status')}</Typography>
                <Typography variant="body1">{selectedViewBanner.isActive ? t('active', 'Active') : t('inactive', 'Inactive')}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="rgba(255,255,255,0.7)">{t('advertising.vip_banners.fields.startDate', 'Start Date')}</Typography>
                <Typography variant="body1">{selectedViewBanner.startDate ? formatUTCDate(selectedViewBanner.startDate, { format: 'date' }) : '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="rgba(255,255,255,0.7)">{t('advertising.vip_banners.fields.endDate', 'End Date')}</Typography>
                <Typography variant="body1">{selectedViewBanner.endDate ? formatUTCDate(selectedViewBanner.endDate, { format: 'date' }) : '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="rgba(255,255,255,0.7)">{t('advertising.vip_banners.fields.displayDate', 'Displayed Date')}</Typography>
                <Typography variant="body1">{selectedViewBanner.displayedDate ? formatUTCDate(selectedViewBanner.displayedDate, { format: 'date' }) : '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="rgba(255,255,255,0.7)">{t('advertising.vip_banners.fields.bannerType', 'Banner Type')}</Typography>
                <Typography variant="body1">{selectedViewBanner.bannerType}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="rgba(255,255,255,0.7)">{t('advertising.vip_banners.fields.actionType', 'Action Type')}</Typography>
                <Typography variant="body1">{selectedViewBanner.actionType || '-'}</Typography>
              </Grid>
              {selectedViewBanner.actionType === 'EXTERNAL_URL' && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="rgba(255,255,255,0.7)">{t('advertising.vip_banners.fields.url', 'URL')}</Typography>
                  <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                    {selectedViewBanner.externalUrl ? (
                      <a href={selectedViewBanner.externalUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#FF69B4', textDecoration: 'none' }}>
                        {selectedViewBanner.externalUrl}
                      </a>
                    ) : '-'}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="rgba(255,255,255,0.7)">{t('advertising.vip_banners.fields.location', 'Location')}</Typography>
                <Typography variant="body1">{selectedViewBanner.location || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="rgba(255,255,255,0.7)">{t('advertising.vip_banners.fields.userType', 'User Type')}</Typography>
                <Typography variant="body1">{selectedViewBanner.userType || '-'}</Typography>
              </Grid>
              {selectedViewBanner.bannerImage && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="rgba(255,255,255,0.7)">{t('advertising.vip_banners.fields.bannerImage', 'Banner Image')}</Typography>
                  <Box mt={1}>
                    <img src={selectedViewBanner.bannerImage} alt="Banner" style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '10px' }} />
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
      </Dialog>


      <GlobalStyles
        styles={{
          '.MuiDataGrid-panel .MuiDataGrid-paper,.MuiPopover-root .MuiPopover-paper, .base-Popper-root .MuiPaper-root, .MuiDialog-root .MuiPaper-root': {
            backgroundColor: '#252525 !important',
            border: '1px solid rgba(230, 210, 249, 0.1)',
            fontSize: '15px',
            color: '#fff',
            borderRadius: '4px',
          },
          '.MuiDialog-root .MuiPaper-root': {
            borderRadius: '20px',
          },
          '.MuiDataGrid-columnsManagementHeader': {
            padding: '10px !important',
          },
          '.MuiInputBase-formControl': {
            fontSize: '14px !important',
            color: 'white !important',
            borderRadius: '999px !important',
          },
          '.MuiInputBase-formControl .MuiOutlinedInput-notchedOutline': {
            border: '1px solid rgba(230, 210, 249, 0.1) !important',
          },
          '.MuiInputBase-formControl .MuiInputAdornment-positionStart svg': {
            fill: 'white',
          },
          '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter': {
            borderTop: '1px solid rgba(230, 210, 249, 0.1)',
            padding: '4px 8px 4px 16px',
          },
          '.MuiDataGrid-paper .MuiDataGrid-columnsManagement': {
            padding: '0px 16px 12px',
          },
          '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiFormControlLabel-label,.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiTypography-root': {
            fontSize: '14px',
          },
          '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiCheckbox-root .MuiSvgIcon-root, .MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiSvgIcon-root': {
            fill: 'white',
          },
          '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiButtonBase-root': {
            color: '#FF69B4',
          },
          '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiDataGrid-filterFormDeleteIcon .MuiSvgIcon-root,.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root svg,.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
            fill: 'white',
          },
          '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiFormLabel-root': {
            color: 'white',
          },
          '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:before': {
            borderColor: 'rgba(230, 210, 249, 0.1) !important',
          },
          '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:after': {
            display: 'none',
          },
          '.MuiPopover-root .MuiPopover-paper .MuiList-root, .base-Popper-root .MuiPaper-root .MuiList-root': {
            padding: '0',
          },
          '.MuiPopover-root .MuiPopover-paper .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiTypography-root': {
            fontSize: '14px',
          },
          '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root': {
            padding: '5px 10px',
          },
          '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root.Mui-selected .MuiListItemIcon-root .MuiSvgIcon-root': {
            fill: '#FF69B4',
          },
          '.base-Popper-root .MuiPaper-root .MuiDivider-root': {
            borderColor: 'rgba(230, 210, 249, 0.3) !important',
            margin: '0',
          },
          '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
            minWidth: '32px',
          },
        }}
      />
    </Box>
  );
}

export default VipBannersManagement;


